import { Injectable } from '@angular/core';
import {
    CentralRegulatorControllerXService,
    CompanyControllerXService,
    CreateBusinessPartnerDocumentQuery
} from '@iota/iota-management-portal-v4-client'; // Assuming this service exists
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { catchError, concatAll, from, mergeMap, of, toArray, withLatestFrom } from 'rxjs';
import {
    SmartDocumentUploadFailure,
    SmartDocumentUploadSubmit,
    SmartDocumentUploadSuccess
} from '../actions/smart-document-upload.actions';
import { smartDocumentUploadFiles } from '../reducer/smart-document-upload.reducer';
import { CompleteSurvey } from '../actions/hwp-survey.actions';
import { CompanyMode } from '../../shared/constants-and-enums/company-mode.enum';
import { HttpResponse } from '@angular/common/http';
import { ReloadRegulator } from '../actions/central-regulators.actions';
import { LoadCompany } from '../actions/company.actions';

@Injectable()
export class SmartDocumentUploadEffects {
    smartDocumentUploadSubmit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SmartDocumentUploadSubmit),
            withLatestFrom(this.store.select(smartDocumentUploadFiles)),
            mergeMap(([{ hwpId, businessPartnerNumber: defaultBusinessPartnerNumber, entityId, mode }, newFiles]) => {
                const filesWithQuery: [File, CreateBusinessPartnerDocumentQuery][] = newFiles.map(
                    ({ file, date, businessPartnerNumber, documentType }): [File, CreateBusinessPartnerDocumentQuery] => {
                        // If the business partner number is empty, set the default company/central regulator number
                        const finalBusinessPartnerNumber = businessPartnerNumber ?? defaultBusinessPartnerNumber;

                        return [
                            new File([new Blob([file], { type: file.type })], file.name, { type: file.type }),
                            {
                                businessPartnerNumber: finalBusinessPartnerNumber,
                                documentType,
                                date: date as any,
                                fileExtension: file.name.split('.').pop(),
                                addDefaultInhUploadLabels: true
                            }
                        ];
                    }
                );

                return from(
                    filesWithQuery.map((blobWithQuery) =>
                        mode !== CompanyMode.REGULATOR
                            ? this.companyApiXService.generateNameAndUpsertCompanyAttachment(
                                  entityId,
                                  blobWithQuery[0],
                                  blobWithQuery[1],
                                  'response'
                              )
                            : this.centralRegulatorApiXService.generateNameAndUpsertCentralRegulatorAttachment(
                                  entityId,
                                  blobWithQuery[0],
                                  blobWithQuery[1],
                                  'response'
                              )
                    )
                ).pipe(
                    // We need to send requests one-by-one, otherwise the names can be duplicated
                    concatAll(),
                    toArray(),
                    mergeMap((responses: HttpResponse<string>[]) => {
                        const documentIds = responses.map((response) => response.body);

                        const resultingActions: Action[] = [
                            SmartDocumentUploadSuccess({ numberOfUploadedDocuments: filesWithQuery.length })
                        ];

                        if (mode === CompanyMode.REGULATOR) {
                            resultingActions.push(ReloadRegulator({ centralRegulatorId: entityId }));
                        } else {
                            resultingActions.push(LoadCompany({ companyId: entityId }));

                            if (hwpId) {
                                resultingActions.push(
                                    CompleteSurvey({
                                        surveyId: hwpId,
                                        reportIds: documentIds
                                    })
                                );
                            }
                        }

                        return resultingActions;
                    }),
                    catchError((error) => of(SmartDocumentUploadFailure({ error })))
                );
            })
        )
    );

    constructor(
        private actions$: Actions,
        private store: Store,
        private companyApiXService: CompanyControllerXService,
        private centralRegulatorApiXService: CentralRegulatorControllerXService
    ) {}
}
